/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { formatDate, presentBathCount, propertyIdPresenter } from '@kaiizen/kai-presenters';
import { ApiPropertyProject, isProjectEditable } from '@kaiizen/kai-pro-api';
import {
  usePropertyHeroForScan,
  useScan,
} from '@kaiizen/kai-pro-api-react-state';

import { getMapLink } from '../../lib';
import { DirectionsIcon } from '../Icons/AllIcons';
import { usePropertyIdLabel } from '../Lib';
import { useProjectTypes } from '../State';
import { LAYOUT2, TYPOGRAPHY2 } from '../Styles';
import { SCAN_STATUS_PRESENTATION_TEXT, ScanStatusPill } from './ScanStatusPill';
import { StatusPill } from './StatusPill';

const PROJECT_CARD_STYLE = css`
  label: PROJECT_CARD_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${LAYOUT2.spacing2}px;
  padding: ${LAYOUT2.spacing1}px;
  background-color: var(--background);
  border-radius: 0;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:has(a:hover)) {
    background-color: var(--background2);
  }
`;

const PROJECT_IMAGE_STYLE = css`
  label: PROJECT_IMAGE_STYLE;
  width: 120px;
  height: 90px;
  border-radius: 0;
  object-fit: cover;
  background-color: var(--background3);
  flex-shrink: 0;
`;

const PROJECT_CONTENT_STYLE = css`
  label: PROJECT_CONTENT_STYLE;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 90px;
  min-width: 0;
`;

const STATUS_LABELS_STYLE = css`
  label: STATUS_LABELS_STYLE;
  display: inline-flex;
  overflow: hidden;
`;

const ADDRESS_ROW_STYLE = css`
  label: ADDRESS_ROW_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  flex: 1;
`;

const ADDRESS_STYLE = css`
  label: ADDRESS_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  color: var(--text);
`;

const PROPERTY_SPECS_STYLE = css`
  label: PROPERTY_SPECS_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
  display: flex;
  gap: ${LAYOUT2.spacing * .75}px;
`;

const PROPERTY_SPECS_VALUE_STYLE = css`
  label: PROPERTY_SPECS_VALUE_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
  display: flex;
`;

const PROJECT_INFO_GRID_STYLE = css`
  label: PROJECT_INFO_GRID_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing2}px;
`;

const INFO_ITEM_STYLE = css`
  label: INFO_ITEM_STYLE;
  ${TYPOGRAPHY2.xSmall}
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing * .25}px;
  color: var(--text2);
`;

const INFO_LABEL_STYLE = css`
  label: INFO_LABEL_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text);
`;

const INFO_VALUE_STYLE = css`
  label: INFO_VALUE_STYLE;
  ${TYPOGRAPHY2.xSmallBold}
  color: var(--text);
`;

const DIRECTIONS_LINK_STYLE = css`
  label: DIRECTIONS_LINK_STYLE;
  color: var(--text2);
  width: ${LAYOUT2.spacing4}px;
  height: ${LAYOUT2.spacing4}px;
  background: var(--background2);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  flex-shrink: 0;
  text-decoration: none;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--background3);
  }
`;

const scanTypeMap: Record<string, string> = {
  initial: 'INITIAL WALK',
  final: 'FINAL WALK',
  changeRequest: 'CHANGE ORDER',
  progress: 'PROGRESS / CLOSE OUT',
};

const LabeledValue = ({ label, value }: { label: string; value: string }) => (
  <div css={INFO_ITEM_STYLE}>
    <div css={INFO_LABEL_STYLE}>{label}</div>
    <div css={INFO_VALUE_STYLE}>{value}</div>
  </div>
);

export const PropertyProjectCard = ({ project, marketName }: { project: ApiPropertyProject; marketName?: string }) => {
  const navigate = useNavigate();
  const { getProjectTypeLabel } = useProjectTypes();
  const { orgProperty } = project;
  const propertyIdLabel = usePropertyIdLabel();

  const updatedAt = project.updatedAt;
  const { scan: firstScan } = useScan(project.firstScan);
  const { scan: latestScan } = useScan(project.latestScan);
  const { scan: latestApprovedScan } = useScan(project.latestApprovedScan);
  const haveScans = !!firstScan;

  const heroUri = usePropertyHeroForScan(latestScan, project);

  const facts = orgProperty?.customerMetadata;
  const bedroomCount = facts?.bedroomCount;
  const squareFeet = facts?.squareFeet;

  const projectTypeLabel = useMemo(() => {
    const projectType = project.customerMetadata?.projectType;
    const label = getProjectTypeLabel(projectType);
    return label ? label.toUpperCase() : null;
  }, [getProjectTypeLabel, project.customerMetadata?.projectType]);

  const scanTypeLabel = useMemo(() => {
    if (!firstScan) return null;
    const scanType = firstScan.scanType;
    if (!scanType) return null;

    const scanTypeLabelText = scanTypeMap[scanType] ?? scanTypeMap.initial;
    const scanStatusLabel = firstScan.scanLifecycleStatus;
    return `${scanTypeLabelText} - ${SCAN_STATUS_PRESENTATION_TEXT[scanStatusLabel]}`;
  }, [firstScan]);

  const address = orgProperty?.address ?? 'Address not available';
  const mapLink = getMapLink(address, facts?.latitude ?? undefined, facts?.longitude ?? undefined);

  const handleDirectionsClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const projectPageUrl = `/project/${project.id}`;
  const dueDate = firstScan?.customerMetadata?.dueDate;
  const due = dueDate ? formatDate(dueDate) : '--';
  const lastUpdated = updatedAt ? formatDate(updatedAt) : '--';

  const statusScan = isProjectEditable(project) ? latestScan : latestApprovedScan;

  return (
    <div css={PROJECT_CARD_STYLE} onClick={() => navigate(projectPageUrl)}>
      {heroUri
        ? (
          <img
            src={heroUri}
            alt={orgProperty?.address || ''}
            css={PROJECT_IMAGE_STYLE}
            onError={error => {
              (error.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )
        : <div css={PROJECT_IMAGE_STYLE} />}
      <div css={PROJECT_CONTENT_STYLE}>
        {(projectTypeLabel || scanTypeLabel) && (
          <div css={STATUS_LABELS_STYLE}>
            {projectTypeLabel && (
              <StatusPill
                statusTypeText={projectTypeLabel}
                color={'darkGray'}
                small
                tight
              />
            )}
            {
              <ScanStatusPill
                status={statusScan?.scanLifecycleStatus ?? (haveScans ? 'inProgress' : 'created')}
                scanType={statusScan?.scanType ?? (haveScans ? 'progress' : 'initial')}
                small
                tight
              />
            }
          </div>
        )}
        <div css={ADDRESS_ROW_STYLE}>
          <div css={ADDRESS_STYLE}>{address}</div>
          <div css={PROPERTY_SPECS_STYLE}>
            <div css={PROPERTY_SPECS_VALUE_STYLE}>{bedroomCount ?? '--'} Bed</div> |
            <div css={PROPERTY_SPECS_VALUE_STYLE}>{presentBathCount(facts?.fullBathCount, facts?.halfBathCount, facts?.bathroomCount)} Bath</div> |
            <div css={PROPERTY_SPECS_VALUE_STYLE}>{squareFeet ? `${squareFeet.toLocaleString()} sqft` : '--'}</div>
          </div>
        </div>
        <div css={PROJECT_INFO_GRID_STYLE}>
          {marketName && <LabeledValue label='Market:' value={marketName} />}
          <LabeledValue label={`${propertyIdLabel}:`} value={propertyIdPresenter(orgProperty)} />
          {project.name && <LabeledValue label='Project Name:' value={project.name} />}
          <LabeledValue label='Date Due:' value={due} />
          <LabeledValue label='Last Updated:' value={lastUpdated} />
        </div>
      </div>
      <a href={mapLink} target='_blank' rel='noopener noreferrer' css={DIRECTIONS_LINK_STYLE} onClick={handleDirectionsClick}>
        <DirectionsIcon color='var(--text2)' />
      </a>
    </div>
  );
};
